<template>
  <div>
    <p class="p-5 pb-0 text-[13px] font-semibold mb-1">
      Welcome to the Living Plans Portal!
    </p>

    <div
      class="[&_*]:mx-5 [&_*]:leading-[17px]"
      :class="{'border-b border-chrome-light': !activeRoleContent.banner}"
    >
      <p class="pb-5 mb-0 text-[13px] w-[350px]">
        {{ activeRoleContent.description }}
      </p>

      <div
        v-if="activeRoleContent.banner"
        class="subtle-secondary-banner min-h-[50px] mb-0 py-3 text-[13px]"
      >
        {{ activeRoleContent.banner }}

        <AppRouterButton
          v-if="activeRoleContent.buttons.includes('approvalButtons')"
          class="btn-outline btn-secondary-filled !py-0.5"
          :to="{ name: 'app_plan_show', params: { district: route.params.district, plan: route.params.plan } }"
          target="_blank"
        >
          View
        </AppRouterButton>
      </div>
    </div>

    <div
      v-if="activeRoleContent.buttons.length > 0"
      class="p-5 pb-6 border-b border-chrome-light [&_.btn]:w-[350px] [&_.btn]:flex  [&_.btn]:text-[13px]"
    >
      <span class="text-xs text-black font-medium block mb-1.5">Actions:</span>

      <AppRouterButton
        v-if="activeRoleContent.buttons.includes('editDocumentButton')"
        :to="{ name: 'app_plan_show', params: { district: route.params.district, plan: route.params.plan } }"
        text="Edit Document"
        class="btn-outline btn-secondary !justify-start !px-4 h-12 group"
      >
        <div class="w-6 h-6 flex items-center justify-center bg-gray-800 border border-gray-800 group-hover:bg-white rounded-full">
          <FontAwesomeIcon
            :icon="faPencil"
            class="text-xs text-white group-hover:text-black"
          />
        </div>
        Edit Document
      </AppRouterButton>

      <div
        v-if="activeRoleContent.buttons.includes('submitForApprovalButton')"
        class="submit-for-approve-button flex items-center justify-between gap-2 w-full mt-3"
      >
        <AppButton
          class="btn-outline btn-secondary !justify-start !px-4 h-12 w-full group"
          @click="$emit('submitForApproval')"
        >
          <FontAwesomeIcon
            :icon="faCheckCircle"
            class="w-6 h-6 text-gray-800 group-hover:text-white"
          />
          Submit the document for approval
        </AppButton>

        <AppTooltip class="[&_.tooltip-body]:w-[160px] [&_.tooltip-body]:text-[11px] [&_.tooltip-body]:pl-[6px]">
          <template #reference>
            <FontAwesomeIcon
              :icon="faCircleInfo"
              class="w-5 h-5 text-gray-800 group-hover:text-white"
            />
          </template>
          Please note that you cannot make edits to the document once you have submitted it.
        </AppTooltip>
      </div>

      <div
        v-if="activeRoleContent.buttons.includes('approveRejectButtons')"
        class="flex gap-2"
      >
        <AppButton
          class="btn-outline btn-secondary !justify-start !px-4 h-12 mt-3 w-full group"
          @click="$emit('approveDocument')"
        >
          <div class="w-6 h-6 flex items-center justify-center">
            <FontAwesomeIcon
              :icon="faCheckCircle"
              class="text-2xl"
            />
          </div>
          Approve document
        </AppButton>

        <AppButton
          class="btn-outline btn-secondary !justify-start !px-4 h-12 mt-3 w-full group"
          @click="$emit('rejectDocument')"
        >
          <div class="w-6 h-6 flex items-center justify-center">
            <FontAwesomeIcon
              :icon="faXmarkCircle"
              class="text-2xl"
            />
          </div>
          Reject document
        </AppButton>
      </div>

      <AppButton
        v-if="activeRoleContent.buttons.includes('downloadPdfButton')"
        class="btn-outline btn-secondary !justify-start !px-4 h-12 mt-3 w-full group"
        :processing="pdfStore.status === 'Pending'"
        @click="$emit('downloadPdf')"
      >
        <div class="w-6 h-6 flex items-center justify-center">
          <FontAwesomeIcon
            :icon="faFilePdf"
            class="text-2xl"
          />
        </div>
        {{ pdfStore.status === 'Pending' ? 'Generating Pdf' : 'Download Pdf' }}
      </AppButton>

      <AppRouterButton
        v-if="activeRoleContent.buttons.includes('viewPdfButton')"
        :to="{ name: 'app_plan_pdf_preview', params: { district: route.params.district, plan: route.params.plan } }"
        class="btn-outline btn-secondary !justify-start !px-4 h-12 mt-3"
        target="_blank"
      >
        <div class="w-6 h-6 flex items-center justify-center">
          <FontAwesomeIcon
            :icon="faFilePdf"
            class="text-2xl"
          />
        </div>
        Preview Document
      </AppRouterButton>
    </div>
  </div>
</template>

<script setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { AppTooltip } from '@/components/app/index.js';
import { AppButton, AppRouterButton } from '@/components/buttons';
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import { faCheckCircle, faCircleInfo, faPencil, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { computed } from 'vue';
import { Role } from '@/const.js';
import { useAuthStore, usePdfStore } from '@/store';
import { useRoute } from 'vue-router';

const authStore = useAuthStore();
const pdfStore = usePdfStore();
const route = useRoute();

defineEmits(['submitForApproval', 'approveDocument', 'rejectDocument', 'downloadPdf']);

const props = defineProps({
  plan: {
    type: Object,
    default: () => ({}),
  },
});

// NOTE: Temporary roleConfig as publication has not been implemented yet
const roleConfig = computed(() => {
  return {
    [Role.Admin]: {
      description: '',
      buttons: ['editDocumentButton', 'viewPdfButton'],
    },
    [Role.Capturer]: {
      description: '',
      banner: null,
      showBannerViewButton: false,
      buttons: ['editDocumentButton', 'viewPdfButton'],
    },
    [Role.Reviewer]: {
      description: '',
      banner: null,
      buttons: ['editDocumentButton', 'viewPdfButton'],
    },
    [Role.Viewer]: {
      description: '',
      banner: null,
      buttons: ['viewPdfButton'],
    },
    public: {
      description: null,
      banner: null,
      buttons: [],
    },
  };
});

const activeRoleContent = computed(() => {
  const role = authStore.user?.roles.find(r => roleConfig.value[r]);
  return roleConfig.value[role] || roleConfig.value.public;
});

</script>
